import {
  ApiResponse,
  Provider,
  PaginationQuery,
  PaginationResponse,
} from '@magnetar/admin-api-interfaces'
import { useAuth } from './useAuth'
import { useGet } from './useFetch'

interface UseProvidersParams extends PaginationQuery {
  search?: string
  is_active?: boolean
}

export function useProviders({
  page = 1,
  pageSize = 10,
  search,
  is_active,
}: UseProvidersParams) {
  const { token } = useAuth()

  const queryParams = new URLSearchParams()
  queryParams.append('page', String(page))
  queryParams.append('pageSize', String(pageSize))
  if (search) queryParams.append('search', search)
  if (is_active !== undefined) queryParams.append('is_active', String(is_active))

  const { data, error, mutate } = useGet<ApiResponse<PaginationResponse<Provider>>>(
    token ? `/api/providers?${queryParams.toString()}` : null,
    undefined,
    token ?? '',
  )

  return {
    providers: data?.data?.items,
    pagination: data?.data
      ? {
          page: data.data.page,
          pageSize: data.data.pageSize,
          total: data.data.total,
          totalPages: data.data.totalPages,
        }
      : undefined,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}

export function useProvider(id: number | null) {
  const { token } = useAuth()
  const { data, error, mutate } = useGet<ApiResponse<Provider>>(
    token && id ? `/api/providers/${id}` : null,
    undefined,
    token ?? '',
  )

  return {
    provider: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}

// Hook to get provider details with relations
export function useProviderDetails(id: number | null) {
  const { token } = useAuth()
  const { data, error, mutate } = useGet<ApiResponse<Provider & {
    clinics?: { clinic_id: number; clinic_name: string }[]
    ad_suppliers?: { supplier_id: number; supplier_name: string }[]
  }>>(
    token && id ? `/api/providers/${id}/details` : null,
    undefined,
    token ?? '',
  )

  return {
    provider: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}

// Hook to get clinics by provider
export function useProviderClinics(providerId: number | null) {
  const { token } = useAuth()
  const { data, error, mutate } = useGet<ApiResponse<{ clinic_id: number; clinic_name: string }[]>>(
    token && providerId ? `/api/providers/${providerId}/clinics` : null,
    undefined,
    token ?? '',
  )

  return {
    clinics: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}

// Hook to get suppliers by provider
export function useProviderSuppliers(providerId: number | null) {
  const { token } = useAuth()
  const { data, error, mutate } = useGet<ApiResponse<{ supplier_id: number; supplier_name: string }[]>>(
    token && providerId ? `/api/providers/${providerId}/suppliers` : null,
    undefined,
    token ?? '',
  )

  return {
    suppliers: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}

// Hook to get current user's providers (for provider_admin users)
export function useMyProviders() {
  const { token } = useAuth()
  const { data, error, mutate } = useGet<
    ApiResponse<{
      providers: Array<{ provider_id: number; provider_name: string; provider_user_id: number; user_id: number }>
    }>
  >(token ? '/api/me/providers' : null, undefined, token ?? '')

  return {
    providers: data?.data?.providers,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}
